import { supabase } from '../supabase/client';
import { KontaktUnified, KontaktQuelle, RohLink, Aktivitaet, KontaktNotiz } from './types';
import { zuAktivitaet, logZuAktivitaet, auditZuAktivitaet } from './aktivitaet';

export async function ladeKontakteUnified(
  opts: { quelle?: KontaktQuelle; suche?: string; nurAktiv?: boolean } = {},
): Promise<KontaktUnified[]> {
  let q = supabase.from('kontakte_unified').select('*').order('sort_name').limit(1000);
  if (opts.quelle) q = q.eq('quelle', opts.quelle);
  if (opts.nurAktiv) q = q.eq('aktiv', true);
  const s = (opts.suche || '').trim();
  if (s) {
    // Gegen PostgREST-Filter-Injection: Wert quoten + " und \ escapen
    const safe = s.replace(/[\\"]/g, '\\$&');
    q = q.or(`anzeigename.ilike."%${safe}%",email.ilike."%${safe}%",telefon.ilike."%${safe}%"`);
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as KontaktUnified[];
}

export async function ladeVerknuepfungen(typ: string, id: string): Promise<RohLink[]> {
  // typ/id sind kontrolliert (Quelle-Enum + UUID), defensiv gegen Filter-Injection validieren
  const typSafe = (typ || '').replace(/[^a-z_]/gi, '');
  const istUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id || '');
  if (!typSafe || !istUuid) return [];
  const { data } = await supabase
    .from('entity_links')
    .select('id, source_type, source_id, target_type, target_id, link_type')
    .or(`and(source_type.eq.${typSafe},source_id.eq.${id}),and(target_type.eq.${typSafe},target_id.eq.${id})`);
  return (data || []) as RohLink[];
}

// Eine Verknüpfung (entity_link) gezielt per id aufheben.
export async function loeseVerknuepfung(linkId: string): Promise<void> {
  const { error } = await supabase.from('entity_links').delete().eq('id', linkId);
  if (error) throw error;
}

export async function ladeNotizen(quelle: KontaktQuelle, id: string): Promise<KontaktNotiz[]> {
  const { data } = await supabase.from('kontakt_notizen')
    .select('id, quelle, quelle_id, text, created_at')
    .eq('quelle', quelle).eq('quelle_id', id)
    .order('created_at', { ascending: false });
  return (data || []) as KontaktNotiz[];
}

export async function speichereNotiz(quelle: KontaktQuelle, id: string, text: string): Promise<void> {
  const { error } = await supabase.from('kontakt_notizen').insert({ quelle, quelle_id: id, text });
  if (error) throw new Error(error.message);
}

export async function loescheNotiz(notizId: string): Promise<void> {
  const { error } = await supabase.from('kontakt_notizen').delete().eq('id', notizId);
  if (error) throw new Error(error.message);
}

export async function ladeAktivitaeten(quelle: KontaktQuelle, id: string): Promise<Aktivitaet[]> {
  const akt: Aktivitaet[] = [];
  // E-Mails: mieter/eigentuemer direkt ueber emails; dienstleister ueber email_analysen
  if (quelle === 'mieter' || quelle === 'eigentuemer') {
    // SCHEDULED/SENDING (noch nicht versendete "Später senden"-Mails) dürfen hier nicht als
    // bereits geschriebene Aktivität auftauchen — Leak-Fix aus dem Task-5-Review.
    const { data } = await supabase.from('emails')
      .select('id, betreff, datum, von, mieter_id, eigentuemer_id')
      .eq(quelle === 'mieter' ? 'mieter_id' : 'eigentuemer_id', id)
      .not('ordner', 'in', '("SCHEDULED","SENDING")')
      .limit(200);
    (data || []).forEach((r) => akt.push(zuAktivitaet('email', r)));
  } else if (quelle === 'dienstleister') {
    const { data } = await supabase.from('email_analysen')
      .select('email_id, emails(id, betreff, datum, von)')
      .eq('dienstleister_id', id).limit(200);
    (data || []).forEach((r: any) => { if (r.emails) akt.push(zuAktivitaet('email', r.emails)); });
  }
  if (quelle === 'mieter') {
    const { data } = await supabase.from('tickets').select('id, titel, status, created_at').eq('mieter_id', id).limit(200);
    (data || []).forEach((r) => akt.push(zuAktivitaet('ticket', r)));
  }
  if (quelle === 'eigentuemer') {
    const { data } = await supabase.from('rechnungen').select('id, bezeichnung, unternehmen, brutto, datum').eq('eigentuemer_id', id).limit(200);
    (data || []).forEach((r) => akt.push(zuAktivitaet('rechnung', r)));
  }
  if (quelle === 'mieter' || quelle === 'eigentuemer') {
    const col = quelle === 'mieter' ? 'mieter_id' : 'eigentuemer_id';
    const { data: dok } = await supabase.from('dokumente').select('id, titel, datei_name, kategorie, created_at').eq(col, id).limit(200);
    (dok || []).forEach((r) => akt.push(zuAktivitaet('dokument', r)));
    const { data: ter } = await supabase.from('termine').select('id, titel, status, start_datum').eq(col, id).limit(200);
    (ter || []).forEach((r) => akt.push(zuAktivitaet('termin', r)));
  }
  // Notizen
  const notizen = await ladeNotizen(quelle, id);
  notizen.forEach((n) => akt.push(zuAktivitaet('notiz', n)));
  // Schnell-Logs (Anruf/Mail/Meeting) aus kontakt_aktivitaeten
  const typSafe = (quelle || '').replace(/[^a-z_]/gi, '');
  if (typSafe) {
    const { data: logs } = await supabase.from('kontakt_aktivitaeten')
      .select('id, typ, richtung, betreff, notiz, dauer_minuten, zeitpunkt')
      .eq('quelle', typSafe).eq('quelle_id', id).limit(300);
    (logs || []).forEach((r: any) => akt.push(logZuAktivitaet(r)));
  }
  // Änderungen (Audit) — physische Tabelle: quelle 'kontakt' liegt in 'kontakte'
  const auditTabelle = quelle === 'kontakt' ? 'kontakte' : typSafe;
  if (auditTabelle) {
    const { data: audit } = await supabase.from('entity_audit')
      .select('id, aktion, feld, alt_wert, neu_wert, geaendert_am')
      .eq('tabelle', auditTabelle).eq('datensatz_id', id).limit(300);
    (audit || []).forEach((r: any) => akt.push(auditZuAktivitaet(r)));
  }
  // nach Datum absteigend
  return akt.sort((a, b) => (b.datum || '').localeCompare(a.datum || ''));
}

export interface LogEingabe {
  typ: 'anruf' | 'mail' | 'meeting';
  richtung?: 'eingehend' | 'ausgehend' | null;
  betreff: string;
  notiz?: string | null;
  dauer_minuten?: number | null;
  zeitpunkt?: string; // ISO; Default = jetzt (DB)
}

// Manuell geloggte Aktivität (Anruf/Mail/Meeting) speichern.
export async function logAktivitaet(quelle: KontaktQuelle, id: string, e: LogEingabe): Promise<void> {
  const { error } = await supabase.from('kontakt_aktivitaeten').insert({
    quelle, quelle_id: id,
    typ: e.typ,
    richtung: e.richtung ?? null,
    betreff: e.betreff.trim(),
    notiz: e.notiz?.trim() || null,
    dauer_minuten: e.dauer_minuten ?? null,
    ...(e.zeitpunkt ? { zeitpunkt: e.zeitpunkt } : {}),
  });
  if (error) throw error;
}

/**
 * Löscht einen Sonstigen Kontakt endgültig. Räumt zuerst die polymorphen
 * (FK-freien) Verweise auf (kontakt_kanaele, entity_links, kontakt_notizen,
 * kontakt_aktivitaeten), dann die kontakte-Zeile. objekt_kontakte hängt per
 * FK ON DELETE CASCADE dran. Schlägt ein Cleanup fehl, bleibt der Kontakt
 * stehen (kein Waisen-Risiko durch Teillöschung).
 */
export async function loescheKontakt(kontaktId: string): Promise<void> {
  const cleanups = [
    supabase.from('kontakt_kanaele').delete().eq('owner_type', 'kontakt').eq('owner_id', kontaktId),
    supabase.from('entity_links').delete().eq('source_type', 'kontakt').eq('source_id', kontaktId),
    supabase.from('entity_links').delete().eq('target_type', 'kontakt').eq('target_id', kontaktId),
    supabase.from('kontakt_notizen').delete().eq('quelle', 'kontakt').eq('quelle_id', kontaktId),
    supabase.from('kontakt_aktivitaeten').delete().eq('quelle', 'kontakt').eq('quelle_id', kontaktId),
  ];
  for (const cleanup of cleanups) {
    const { error } = await cleanup;
    if (error) throw new Error(error.message);
  }
  const { error } = await supabase.from('kontakte').delete().eq('id', kontaktId);
  if (error) throw new Error(error.message);
}
